import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '../../Box';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { idBuilder, isValueFalsey } from '../../internal';
import { ComboBoxInputChipsContainer } from './ComboBoxInputChipsContainer';
import { ClearButton } from './ClearButton';
import { OpenButton } from './OpenButton';
import { ComboBoxSelector } from './ComboBoxSelector';
import { ListBox } from '../../ListBox';
import { buildListBoxOptions } from './utils/buildListBoxOptions';
import { interstateInputEventBuilder } from '../../InterstateEvents';
import { ReadOnlyComboBox } from './ReadOnlyComboBox';
import { ClickAwayListener } from '@mui/material';
import { nanoid } from 'nanoid';
import { isFieldRequired } from '../../Validation';
import { useValidator } from '../../Validation/useValidator';
import { comboBoxRuleSet } from './comboBoxRuleSet';
import { InputLayout } from '../../InputLayout/InputLayout';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const activeOptions = options => {
  return options.map(o => {
    if (!o.groupHeader && !o.disabled) {
      return o;
    }
  }).filter(Boolean);
};
/**
 * Resolves the style based on the given theme.
 *
 * @param {InterstateTheme} theme - The theme to be used for resolving the style.
 * @returns {Object} - The resolved style object.
 */
const resolveStyle = (theme, layout) => ({
  display: layout === 'horizontal' ? 'inline-flex' : 'flex',
  flexDirection: layout === 'horizontal' ? 'row' : 'column',
  width: '100%',
  gap: '4px'
});

/**
 * The ComboBox component allows users to select from a list of options or input a custom value.
 */
export const ComboBox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    sx,
    size = 'medium',
    id,
    displayLabel = true,
    label,
    layout,
    required,
    disabled = false,
    multiple = false,
    placeholder,
    value,
    maxHeight,
    options,
    allowNewEntry = false,
    onChange,
    name,
    readOnly,
    selectionLimit,
    maxListBoxHeight,
    isLoading,
    loadingMessage,
    displayNoDataMessage = true,
    noDataMessage,
    hasError,
    errorMessage,
    helpMessage,
    customErrorMessages,
    onBlur,
    ...rest
  } = props;
  const theme = useInterstateTheme();
  const initClickAway = useRef(false);
  const inputRef = useRef(null);
  // Holds the state of the list box
  const openRef = useRef(false);
  // Ref for calling open and close on list box
  const listBoxRef = useRef(null);
  // Used to trigger a render of components.  We are trying to control the rendering trigger in this case.
  const [renderNow, setRenderNow] = useState(Date.now());
  const [_selectedOptions, setSelectedOptions] = useState([]);
  const [_inputValue, setInputValue] = useState('');
  const [_collapse, setCollapse] = useState(true);
  const optionsRef = useRef([]);
  const [_newEntryMessage, setNewEntryMessage] = useState(undefined);

  // contains the item selected from the list box.  It is updated in the handleOnSelect callback
  const [_selectedOption, setSelectedOption] = useState();
  const {
    validationState,
    validator
  } = useValidator(Boolean(hasError), errorMessage, false, undefined, customErrorMessages, helpMessage, isFieldRequired(required), label);
  useEffect(() => {
    validator.reset();
  }, [hasError, customErrorMessages, helpMessage, errorMessage]);
  /**
   * Sets the options for the ListBox.
   *
   * @param {ListBoxOptions} options - The options to set.
   * @returns {void}
   */
  const setOptions = options => {
    optionsRef.current = options;
  };

  /**
   * Handles processing of new selectedOption
   */
  useEffect(() => {
    // if the selection option changed to undefined the stop processing and clear new entry message
    if (!_selectedOption) {
      setNewEntryMessage(undefined);
      return;
    }
    // does the option exist in the list of options
    let newOption = options.find(o => o.value === _selectedOption);

    // if it doesn't exist and new entries aren't allowed then stop processing
    if (!newOption && !allowNewEntry) {
      return;
    }
    // we have an entry, allowNewEntry is true, create a new entry from new selected entry if it isn't in options list
    newOption = newOption ? newOption : {
      label: _selectedOption,
      value: _selectedOption
    };

    // if it is a multi then add to the existing list otherwise start with a new list
    const newSelectedOptions = multiple ? JSON.parse(JSON.stringify(_selectedOptions)) : [];

    // add it to the list
    newSelectedOptions.push(newOption);

    // callOnChange and inform app of new chips selected
    callOnChange(newSelectedOptions);

    // remove it from the list box options
    const newOptions = buildListBoxOptions(newSelectedOptions, options, handleOnSelect, undefined);
    setSelectedOption(undefined);
    setSelectedOptions(newSelectedOptions);
    setOptions(newOptions);
    setListBoxState(false, 'handleOnSelect');
    setInputFocus(200);
    setInputValue('');
  }, [_selectedOption]);
  /**
   * Handles the select event of a list box item.
   * Adds the selected option to the chip list and removes it from the list box options.
   *
   * @param {InterstateOnChangeEvent<ListBoxItemEvent>} e - The select event object.
   */
  const handleOnSelect = e => {
    setSelectedOption(e.target.value.itemValue);
  };
  useEffect(() => {
    if (isLoading && !disabled) {
      setListBoxState(true, 'isLoading');
    }
  }, [isLoading, disabled]);
  /**
   * create the selected options that will display as filter chips
   * create the listBox options that will display in the listBox
   */
  useEffect(() => {
    // from the value property build the  selectedOptions
    const selectedOptions = value ? JSON.parse(JSON.stringify(value)) : [];
    // update options with selected value
    // build the listBox options from the options property
    const newOptions = buildListBoxOptions(selectedOptions, options, handleOnSelect, undefined);
    setSelectedOptions(selectedOptions);
    setOptions(newOptions);
  }, [value, options]);
  const callOnChange = selectedOptions => {
    if (onChange) {
      onChange(interstateInputEventBuilder(name, selectedOptions, true, undefined, label, validationState.messageKey));
    }
  };
  const comboBoxContainerRef = useRef(null);
  const effectiveDataTestId = idBuilder('ids-combo-box', rest['data-testid']);
  /**
   * Handle the click event on a chip element.
   *
   * @param {string} e - The value of the clicked chip.
   * @returns {void}
   */
  const handleOnChipClick = e => {
    // remove option from array
    const newSelectedOptions = _selectedOptions.filter(o => o.value !== e);
    // update selected status of list box
    const newOptions = buildListBoxOptions(newSelectedOptions, options, handleOnSelect, undefined);
    callOnChange(newSelectedOptions);
    setSelectedOptions(newSelectedOptions);
    setOptions(newOptions);
    setListBoxState(false, 'handleOnChipClick');
    setInputFocus();
    initClickAway.current = true;
  };
  /**
   * Function to handle clearing of chips.
   *
   * @function handleOnClearChips
   * @returns {void}
   */
  const handleOnClearChips = () => {
    initClickAway.current = true;
    const newOptions = buildListBoxOptions([], options, handleOnSelect, undefined);
    setSelectedOption(undefined);
    setListBoxState(false, 'handleOnClearChips');
    callOnChange([]);
    setTimeout(() => {
      setOptions(newOptions);
      setSelectedOptions([]);
      setInputFocus();
      setInputText('');
    }, 200);
  };
  const handleOnOpenClick = e => {
    e.stopPropagation();
    initClickAway.current = true;
    handleClick();
  };
  const handleOnInputFocus = () => {
    initClickAway.current = true;
    setNewEntryMessage(undefined);
    setCollapse(false);
    validator.reset();
  };

  /**
   * Handles the click away action for a component.
   * Updates state values and performs validation if necessary.
   */
  const handleClickAway = e => {
    if (!initClickAway.current) {
      return;
    } else {
      initClickAway.current = false;
    }
    // Need to be sure that the target is valid
    if (e?.target?.className && typeof e.target.className === 'string') {
      // this classname should match the classname used in ListBoxItem
      if (e.target.className.includes('ids-list-box-group-header') || e.target.className.includes('ids-list-box-item')) {
        return;
      }

      // this classname should match the classname used in ListBoxItem
      if (!e.target.className.includes('ids-list-box-item')) {
        setCollapse(true);
      }
    } else {
      // if target isn't valid then go ahead and collapse
      setCollapse(true);
    }
    setRenderNow(Date.now());
    setListBoxState(false, 'handleClickAway');
    // add validation here
    const validationState = validator.validate(_selectedOptions, {}, comboBoxRuleSet, true);
    if (onBlur) {
      onBlur(interstateInputEventBuilder(name, _selectedOptions, validationState.isValid, e, label, validationState.messageKey));
    }
    if (onChange && !validationState.isValid) {
      onChange(interstateInputEventBuilder(name, _selectedOptions, validationState.isValid, e, label, validationState.messageKey));
    }
    setInputText('');
    setNewEntryMessage(undefined);
    const newOptions = buildListBoxOptions(_selectedOptions, options, handleOnSelect, undefined);
    setOptions(newOptions);
  };
  const handleNewEntry = value => {
    const newEntry = {
      label: value,
      value
    };

    // has the value been entered already
    const prevAllowedEntry = _selectedOptions.find(o => o.value === value || o.label === value);
    if (prevAllowedEntry) {
      return;
    }
    setSelectedOption(newEntry.value);
  };
  /**
   * Handles key down events for input fields.
   *
   * @param {KeyboardEvent} event - The key down event.
   */
  const handleInputKeyDown = event => {
    const keyList = ['ArrowDown', 'ArrowUp', 'Escape', 'Enter'];
    if (event.key === 'ArrowDown' && !isListBoxOpen()) {
      event.preventDefault();
      event.stopPropagation();
      openListBox();
      setInputFocus();
    } else if (isListBoxOpen() && keyList.includes(event.key)) {
      // send down arrow, up arrow, escape to list box
      listBoxRef.current.doKeyAction(event.key);
      setInputFocus();
      event.preventDefault();
      event.stopPropagation();
    }
    // Add new entries to the list of selected options.  This should add a new Chip
    if (event.key === 'Enter' && allowNewEntry && event.target && !isValueFalsey(event.target.value)) {
      const matchingOption = optionsRef.current.find(o => o.value === event.target.value);
      if (!matchingOption) {
        handleNewEntry(event.target.value);
      }
      return;
    }
    // If only one item in options then select it
    if (event.key === 'Enter' && isListBoxOpen() && event.target) {
      const matchingOption = optionsRef.current.find(o => o.value === event.target.value);
      if (matchingOption) {
        setSelectedOption(matchingOption.value);
      }
      const activeOptionList = activeOptions(optionsRef.current);
      if (activeOptionList && activeOptionList.length === 1 && activeOptionList[0]) {
        setSelectedOption(activeOptionList[0].value);
      }
    }
    if (event.key === 'Tab') {
      handleClickAway();
    }
    if (event.key === 'Backspace') {
      if (multiple) {
        let curInputValue = '';
        setInputValue(cur => {
          curInputValue = cur;
          return cur;
        });
        if (curInputValue.length > 0) {
          return;
        }
        const slicedOptions = _selectedOptions.slice(0, _selectedOptions.length - 1);
        const newOptions = buildListBoxOptions(slicedOptions, options, handleOnSelect, undefined);
        callOnChange(slicedOptions);
        setSelectedOptions(slicedOptions);
        setOptions(newOptions);
        setNewEntryMessage(undefined);
      } else if (!multiple && (event.target.value.length < 2 || event.target.selectionStart === 0)) {
        // for single select reset selected options when the input is cleared with backspace
        callOnChange([]);
        setSelectedOptions([]);
        setNewEntryMessage(undefined);
      }
    }
  };

  // handle the key events to control the listBox
  useEffect(() => {
    inputRef.current?.addEventListener('keydown', handleInputKeyDown);
    return () => inputRef.current?.removeEventListener('keydown', handleInputKeyDown);
  }, [multiple, allowNewEntry, _selectedOptions, required]);
  useEffect(() => {
    validator.reset();
  }, [required]);
  /**
   * Handles the click event on the input element.
   * It toggles the list box state and sets focus on the input field.
   * @function handleOnInputClick
   */
  const handleOnInputClick = e => {
    if (disabled) {
      return;
    }
    setListBoxState(!openRef.current, 'onInputClick');
    setInputFocus(200);
    setRenderNow(Date.now());
  };
  /** ************* ListBox Handler ******************/
  /**
   * Sets the state of a list box component.
   *
   * @param {boolean} state - The state to set (true to open, false to close).
   * @param {string} [reason] - Optional reason for setting the state. Useful for debugging
   *
   * @returns {void}
   */
  const setListBoxState = (state, reason) => {
    if (state) {
      listBoxRef.current?.open();
      openRef.current = true;
    } else {
      listBoxRef.current?.close();
      openRef.current = false;
    }
  };
  /**
   * Sets the focus on an input field with an optional delay.
   * @param {number} [delay] - The delay (in milliseconds) before setting the focus. If not provided, the focus is set immediately.
   */
  const setInputFocus = delay => {
    if (!delay) {
      inputRef.current?.focus();
    }
    setTimeout(() => inputRef.current?.focus(), delay);
  };
  const setListBoxOptions = () => {
    listBoxRef.current.updateList(optionsRef.current);
  };

  /**
   * Function to open the list box.
   *
   * @function openListBox
   * @returns {undefined}
   */
  const openListBox = () => {
    validator.reset();
    setListBoxState(true, 'openListBox');
    setRenderNow(Date.now());
    setListBoxOptions();
    setInputFocus();
  };
  /**
   * Returns a boolean value indicating whether the list box is open.
   *
   * @return {boolean} True if the list box is open, false otherwise.
   */
  const isListBoxOpen = () => {
    return Boolean(openRef.current);
  };

  /**
   * Callback function to handle closing of a list box.
   * If list box is open, it calls setListBoxState to close the list box
   * and invokes setInputFocus to set focus on the input field.
   *
   * @function
   * @name handleClose
   */
  const handleClose = useCallback(() => {
    if (isListBoxOpen()) {
      setListBoxState(false, 'handleClose');
    }
    setInputFocus();
  }, []);

  /**
   * Handles the click event for a certain action.
   * If the list box is open, it will set the list box state to false,
   * invoke the setInputFocus function, and perform other actions.
   * If the list box is not open, it will open the list box.
   */
  const handleClick = useCallback(() => {
    if (isListBoxOpen()) {
      setListBoxState(false, 'handleClick');
      setInputFocus();
    } else {
      openListBox();
    }
    setRenderNow(Date.now());
  }, []);

  /**
   * handleOnInputChange function is responsible for handling the input change event in a ComboBox component.
   * It takes an event of type ChangeEvent<HTMLInputElement> as a parameter.
   *
   * @param {ChangeEvent<HTMLInputElement>} event - The input change event object.
   */
  const handleInputChange = event => {
    if (!isListBoxOpen()) {
      openListBox();
    }
    let optionsToFilter = [];
    if (multiple || isValueFalsey(event.target.value)) {
      optionsToFilter = _selectedOptions;
    }
    const newOptions = buildListBoxOptions(optionsToFilter, options, handleOnSelect, event.target.value);
    // if options and new options are same size then there wasn't a match
    if (allowNewEntry && newOptions.length === 0) {
      setNewEntryMessage({
        message: event.target.value,
        displayAllowNewEntry: event.target.value.length > 0
      });
    }
    setOptions(newOptions);
    setListBoxOptions();
    setInputValue(event.target.value);
    setInputFocus();
  };

  /**
   * Sets the input text value.
   *
   * @param {string} text - The text to set as the input value.
   */
  const setInputText = text => {
    setInputValue(text);
  };
  const effectiveInputId = idBuilder('ids-combo-box-input', id || nanoid());

  /**
   * Handles the click event for allowing new entry.
   * If the new entry message is displayed and contains a message,
   * calls the handleNewEntry function with the message.
   *
   * @function
   * @name handleOnAllowNewEntryClick
   */
  const handleOnAllowNewEntryClick = () => {
    if (_newEntryMessage?.displayAllowNewEntry && _newEntryMessage?.message) {
      handleNewEntry(_newEntryMessage.message);
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ClickAwayListener, {
      onClickAway: handleClickAway,
      children: /*#__PURE__*/_jsx(Box, {
        className: 'ids-combo-box-root-container',
        id: id || 'ids-combo-box',
        "data-testid": effectiveDataTestId,
        sx: sx,
        children: /*#__PURE__*/_jsx(InputLayout, {
          layout: layout || 'vertical',
          labelClassName: 'ids-combobox-label',
          required: required,
          size: size,
          labelId: effectiveInputId,
          displayLabel: displayLabel,
          validationState: validationState,
          readOnly: Boolean(readOnly),
          disabled: disabled,
          label: label,
          children: readOnly ? /*#__PURE__*/_jsx(ReadOnlyComboBox, {
            multiple: multiple,
            selectedOptions: _selectedOptions,
            size: size,
            id: id,
            "data-testid": rest['data-testid']
          }) : /*#__PURE__*/_jsxs(ComboBoxInputChipsContainer, {
            size: size,
            multiple: multiple,
            disabled: disabled,
            hasError: !validationState?.isValid,
            ref: comboBoxContainerRef,
            onClick: handleOnInputClick,
            children: [/*#__PURE__*/_jsx(ComboBoxSelector, {
              ref: inputRef,
              multiple: multiple,
              placeholder: placeholder,
              size: size,
              disabled: disabled,
              selectedOptions: _selectedOptions,
              maxHeight: maxHeight,
              onChipClick: handleOnChipClick,
              onInputFocus: handleOnInputFocus,
              onInputChange: handleInputChange,
              onInputClick: handleOnInputClick,
              inputValue: _inputValue,
              selectionLimit: selectionLimit,
              "data-testid": rest['data-testid'],
              id: effectiveInputId,
              collapse: _collapse
            }), multiple && (_inputValue || _selectedOptions.length > 0) && /*#__PURE__*/_jsx(ClearButton, {
              size: size,
              disabled: Boolean(disabled),
              onClear: handleOnClearChips
            }), /*#__PURE__*/_jsx(OpenButton, {
              size: size,
              disabled: Boolean(disabled),
              onClick: handleOnOpenClick,
              listBoxIsOpen: isListBoxOpen(),
              hasError: !validationState?.isValid,
              readOnly: Boolean(readOnly)
            }, renderNow)]
          })
        })
      })
    }), comboBoxContainerRef.current && !readOnly && /*#__PURE__*/_jsx(ListBox, {
      name: 'ids-combo-box-list-box',
      onClose: handleClose,
      anchorEl: comboBoxContainerRef,
      ref: listBoxRef,
      options: optionsRef.current,
      defaultOpenClickOverride: true,
      autoFocusItem: false,
      minWidth: 'anchorWidth',
      size: size,
      maxHeight: maxListBoxHeight,
      isLoading: isLoading,
      loadingMessage: loadingMessage,
      displayNoDataMessage: displayNoDataMessage,
      noDataMessage: noDataMessage,
      displayAllowNewEntry: _newEntryMessage?.displayAllowNewEntry,
      allowNewEntryMessage: _newEntryMessage?.message,
      messageOnClick: handleOnAllowNewEntryClick
    })]
  });
});
ComboBox.displayName = 'ComboBox';